import { Pagination, Rsp } from "@repo/types";
import { User } from "@repo/types";
import { getHttpClient } from "./http";

export const UserAPI = {
  // ── 核心用户操作（两端共有）────────────────────────────────────

  getUserInfo() {
    return getHttpClient().get<Rsp<User.Info>>({ url: "/user/userinfo" });
  },

  getUserProfile(id: string) {
    return getHttpClient().get<Rsp<User.Profile>>({ url: `/user/profile/${id}` });
  },

  searchUser(keyword: string) {
    return getHttpClient().get<Rsp<Pagination<User.UserSearchItem[]>>>({
      url: `/user/v1/search?keyword=${keyword}`,
    });
  },

  reqCosUploadPath(data: User.CosBizParam) {
    return getHttpClient().get<Rsp<User.BucketPath>>({
      url:
        `/user/upload/v2/path?biz=${data.biz}` +
        (data.order_id ? `&order_id=${data.order_id}` : ""),
    });
  },

  batchQueryByQQ(qqNumbers: string[]) {
    return getHttpClient().post<Rsp<User.BatchQueryQQResult>>({
      url: "/user/batch_query_by_qq",
      data: { qq_numbers: qqNumbers },
    });
  },

  batchQueryByUIDs(uids: string[]) {
    return getHttpClient().post<Rsp<User.BatchQueryUIDResult>>({
      url: "/user/batch_query_by_uid",
      data: { uids },
    });
  },

  updateUserInfo(data: Partial<User.Info>) {
    return getHttpClient().post<Rsp<User.Info>>({
      url: "/user/userinfo",
      data,
    });
  },

  /** v2 增量更新资料（仅提交存在的字段） */
  updateUserInfoV2(data: User.ProfileUpdatePayload) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/v2/profile/userinfo",
      data,
    });
  },

  updateAvatar(avatarUrl: string) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/avatar",
      data: { avatar_url: avatarUrl },
    });
  },

  /** v2 头像更新：STS 上传到 temp/{uid}/ 后提交 file_key */
  updateAvatarV2(fileKey: string) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/v2/profile/avatar",
      data: { file_key: fileKey },
    });
  },

  getPrivacySettings() {
    return getHttpClient().get<Rsp<User.PrivacySettings>>({
      url: "/user/settings/privacy",
    });
  },

  updatePrivacySettings(data: Partial<User.PrivacySettings>) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/settings/privacy",
      data,
    });
  },

  getNotificationSettings() {
    return getHttpClient().get<Rsp<User.NotificationSettings>>({
      url: "/user/settings/notification",
    });
  },

  updateNotificationSettings(data: Partial<User.NotificationSettings>) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/settings/notification",
      data,
    });
  },

  getBlacklist(page = 1, pageSize = 20) {
    return getHttpClient().get<Rsp<Pagination<User.BlacklistUser[]>>>({
      url: `/user/blacklist?page=${page}&page_size=${pageSize}`,
    });
  },

  removeBlacklist(uid: string) {
    return getHttpClient().delete<Rsp<null>>({
      url: `/user/blacklist?uid=${encodeURIComponent(uid)}`,
    });
  },

  // ── webview 扩展 ─────────────────────────────────────────────

  updatePhone(
    uid: string,
    phoneNumber: string,
    smsCode: string,
    countryCode: string,
    sessionCode: string,
  ) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/phone_update",
      data: { uid, phone_number: phoneNumber, code: smsCode, country_code: countryCode, session_code: sessionCode },
    });
  },

  submitFeedback(data: {
    description: string;
    contact?: string;
    attachments?: string[];
  }) {
    return getHttpClient().post<Rsp<{ id: string }>>({
      url: "/user/feedback",
      data: { description: data.description, contact: data.contact ?? "", attachments: data.attachments ?? [] },
    });
  },

  // ── 勋章相关 ─────────────────────────────────────────────────

  getBadgeList() {
    return getHttpClient().get<Rsp<User.Badge[]>>({ url: "/user/badge/list" });
  },

  getBadgeDetail(id: string) {
    return getHttpClient().get<Rsp<User.Badge>>({ url: `/user/badge/detail/${id}` });
  },

  getCollectableBadgeList() {
    return getHttpClient().get<Rsp<User.Badge[]>>({ url: "/user/badge/collectable" });
  },

  collectBadge(eventId: string) {
    return getHttpClient().get<Rsp<User.Badge>>({ url: `/user/badge/collect?event_id=${eventId}` });
  },

  // ── 账号恢复 ─────────────────────────────────────────────────

  findUserByPhone(countryCode: string, phoneNumber: string) {
    return getHttpClient().post<Rsp<{ ticket: string; expire: number }>>({
      url: "/user/recovery/find_uid_by_phone",
      data: { country_code: countryCode, phone_number: phoneNumber },
    });
  },

  completePhoneRecovery(data: {
    recovery_ticket: string;
    session_code: string;
    country_code: string;
    phone_number: string;
    code: string;
  }) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/recovery/complete_phone",
      data,
    });
  },

  getRecoveryUserInfo(countryCode: string, phoneNumber: string) {
    return getHttpClient().get<Rsp<{ avatar: string; nickname: string; real_id: boolean; uid: string }>>({
      url: `/user/recovery?country_code=${countryCode}&phone_number=${phoneNumber}`,
    });
  },

  findUserByRealName(realName: string, idNumber: string) {
    return getHttpClient().post<Rsp<{ ticket: string; expire: number }>>({
      url: "/user/recovery/find_uid_by_real_id",
      data: { real_name: realName, id_number: idNumber },
    });
  },

  getRecoveryFaceIdToken(ticket: string) {
    return getHttpClient().post<
      Rsp<{
        faceId: string;
        bizSeqNo: string;
        transactionTime: string;
        orderNo: string;
        success: unknown;
        nonce: string;
        sign: string;
        uid: string;
      }>
    >({
      url: "/user/recovery/face_id/token",
      data: { recovery_ticket: ticket },
    });
  },

  verifyRecoveryFaceId(ticket: string, orderNo: string, nonce: string) {
    return getHttpClient().post<Rsp<{ session_code: string; expire: number }>>({
      url: "/user/recovery/face_id/verify",
      data: { recovery_ticket: ticket, orderNo, nonce },
    });
  },

  completeRecovery(data: {
    recovery_ticket: string;
    session_code: string;
    country_code: string;
    phone_number: string;
    code: string;
    password: string;
  }) {
    return getHttpClient().post<Rsp<null>>({
      url: "/user/recovery/complete",
      data,
    });
  },
};
